/**
 * A guest's own local record of the versus matches they have played,
 * broken out by `RoomVariant`.
 *
 * A signed-in player's versus record is derived server-side, from `match`/
 * `match_participant` (`GET /api/account/stats`) — proved by the match the
 * server itself refereed, never a client's own claim. A guest has no account
 * for the server to derive anything from, so this is the honest fallback: the
 * client remembers what it personally saw happen, on this device, and nothing
 * more. Signing in does not merge this store up — there is nowhere on the
 * server for an unverified local tally to go that the real, derived record
 * does not already cover better.
 *
 * A plain module, not a `.svelte.ts` one, for the same testability reason
 * every other `*-io.ts` module in this directory gives.
 */
import { safeLocalStorage } from './settings-io.js';

export const VERSUS_STATS_STORAGE_KEY = 'interlock:versus-stats:v1';

/**
 * The same store, for matches against the server's own AI.
 *
 * **A second key rather than a second field**, and that is the whole design
 * decision here. Widening `VariantStats` (or keying entries `duel:ai`) would
 * mean every device that has ever played a duel carries a `v1` blob this build
 * has to migrate or misread; a fresh key is empty for everybody, cannot
 * collide, and leaves the existing store byte-for-byte what it was. It also
 * mirrors what the server does for a signed-in player — `VariantRecord.vsAi`
 * splits the rows rather than summing them — so the two halves of the stats
 * screen are fed the same shape from either source.
 */
export const VERSUS_AI_STATS_STORAGE_KEY = 'interlock:versus-stats-ai:v1';

export interface VariantStats {
  readonly played: number;
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
}

export type VersusStatsStore = Record<string, VariantStats>;

const EMPTY_VARIANT_STATS: VariantStats = { played: 0, wins: 0, losses: 0, draws: 0 };

function readStore(key: string, storage: Storage | undefined): VersusStatsStore {
  if (storage === undefined) return {};
  try {
    const raw = storage.getItem(key);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as VersusStatsStore) : {};
  } catch {
    return {};
  }
}

function writeStore(key: string, store: VersusStatsStore, storage: Storage | undefined): void {
  if (storage === undefined) return;
  try {
    storage.setItem(key, JSON.stringify(store));
  } catch {
    // Storage full or blocked — stop persisting, don't interrupt play.
  }
}

export function loadAllVersusStats(
  storage: Storage | undefined = safeLocalStorage(),
): VersusStatsStore {
  return readStore(VERSUS_STATS_STORAGE_KEY, storage);
}

/** The same, for matches against the AI. See `VERSUS_AI_STATS_STORAGE_KEY`. */
export function loadAllVersusAiStats(
  storage: Storage | undefined = safeLocalStorage(),
): VersusStatsStore {
  return readStore(VERSUS_AI_STATS_STORAGE_KEY, storage);
}

export function loadVariantStats(
  variant: string,
  storage: Storage | undefined = safeLocalStorage(),
): VariantStats {
  return readStore(VERSUS_STATS_STORAGE_KEY, storage)[variant] ?? EMPTY_VARIANT_STATS;
}

/**
 * A witnessed match ended. Called once per finished match from the duel
 * screen, and only for a guest — a signed-in player's copy of the same match
 * is already durable server-side (`match_participant`), so recording it here
 * too would be a second, weaker ledger of a fact the server already has
 * better.
 */
export function recordVersusResult(
  variant: string,
  outcome: 'win' | 'loss' | 'draw',
  storage: Storage | undefined = safeLocalStorage(),
): VariantStats {
  return record(VERSUS_STATS_STORAGE_KEY, variant, outcome, storage);
}

/**
 * A witnessed match against the server's own AI ended.
 *
 * A separate function rather than a flag on the one above, so that every call
 * site says out loud which ledger it is writing to — the two are deliberately
 * never added together, and a boolean argument at a call site is exactly how
 * that stops being obvious.
 */
export function recordVersusAiResult(
  variant: string,
  outcome: 'win' | 'loss' | 'draw',
  storage: Storage | undefined = safeLocalStorage(),
): VariantStats {
  return record(VERSUS_AI_STATS_STORAGE_KEY, variant, outcome, storage);
}

function record(
  key: string,
  variant: string,
  outcome: 'win' | 'loss' | 'draw',
  storage: Storage | undefined,
): VariantStats {
  const store = readStore(key, storage);
  const current = store[variant] ?? EMPTY_VARIANT_STATS;
  const next: VariantStats = {
    played: current.played + 1,
    wins: current.wins + (outcome === 'win' ? 1 : 0),
    losses: current.losses + (outcome === 'loss' ? 1 : 0),
    draws: current.draws + (outcome === 'draw' ? 1 : 0),
  };
  store[variant] = next;
  writeStore(key, store, storage);
  return next;
}

export function hasAnyMatches(stats: VariantStats): boolean {
  return stats.played > 0;
}
